import type { ElementType } from "react";
import { databases, DATABASE_ID, COLLECTIONS, Query } from "./appwrite";
import {
  BookOpen, Calculator, Atom, TrendingUp, FlaskConical, Globe, Beaker,
} from "lucide-react";
import type { Subject, Module, Topic, Subtopic } from "@/data/subjects";

// ─── Display-name helpers ───────────────────────────────────────────────────

const DISPLAY_OVERRIDES: Record<string, string> = {
  "ap-physics-1":  "AP® Physics 1",
  "ap-physics-2":  "AP® Physics 2",
  "ap-calc-bc":    "AP® Calculus BC",
  "ap-calc-ab":    "AP® Calculus AB",
  "ap-chem":       "AP® Chemistry",
  "ap-bio":        "AP® Biology",
  "ap-enviro":     "AP® Environmental Science",
  "sat":           "SAT®",
  "act":           "ACT®",
  "ap_physics_1":  "AP Physics 1",
  "ap_physics_2":  "AP Physics 2",
  "linear_momentum":              "Linear Momentum",
  "torque_and_rotational_dynamics": "Torque & Rotational Dynamics",
  "work_energy_and_power":        "Work, Energy & Power",
  "oscillations":                 "Oscillations",
  "force_and_translational_dynamics": "Force & Translational Dynamics",
  "free_body_diagrams":           "Free Body Diagrams",
  "newton_s_laws_of_motion":      "Newton's Laws of Motion",
};

function idToName(id: string): string {
  if (DISPLAY_OVERRIDES[id]) return DISPLAY_OVERRIDES[id];
  return id
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bAp\b/g, "AP")
    .replace(/\bSat\b/g, "SAT")
    .replace(/\bAct\b/g, "ACT")
    .replace(/\bEla\b/g, "ELA");
}

// ─── Subject metadata (colors, gradients, icons) ────────────────────────────

interface SubjectMeta {
  color: string;
  gradient: string;
  description: string;
  defaultIcon: ElementType;
}

const MODULE_ICON_MAP: Record<string, ElementType> = {
  math: Calculator,
  reading_and_writing: BookOpen,
  reading: BookOpen,
  science: FlaskConical,
  ap_physics_1: Atom,
  mechanics: Atom,
  calculus: TrendingUp,
  history: Globe,
  chemistry: Beaker,
};

const SUBJECT_META: Record<string, SubjectMeta> = {
  sat: {
    color: "bg-blue-500",
    gradient: "from-blue-500 to-cyan-500",
    description: "Prepare for the digital SAT with comprehensive Math and Reading & Writing practice.",
    defaultIcon: Calculator,
  },
  act: {
    color: "bg-red-500",
    gradient: "from-red-500 to-orange-500",
    description: "Master the ACT with detailed questions for English, Math, Reading, and Science.",
    defaultIcon: BookOpen,
  },
  "ap-physics-1": {
    color: "bg-violet-500",
    gradient: "from-violet-500 to-purple-500",
    description: "Algebra-based physics covering mechanics, waves, and rotational motion.",
    defaultIcon: Atom,
  },
  "ap-physics-2": {
    color: "bg-purple-600",
    gradient: "from-purple-600 to-indigo-600",
    description: "Fluid dynamics, thermodynamics, electricity, magnetism, optics, and modern physics.",
    defaultIcon: Atom,
  },
  "ap-calc-bc": {
    color: "bg-emerald-500",
    gradient: "from-emerald-500 to-teal-500",
    description: "Comprehensive calculus covering limits, derivatives, integrals, and series.",
    defaultIcon: TrendingUp,
  },
  "ap-calc-ab": {
    color: "bg-teal-500",
    gradient: "from-teal-500 to-green-500",
    description: "Differential and integral calculus fundamentals.",
    defaultIcon: TrendingUp,
  },
};

const DEFAULT_COLORS = [
  { color: "bg-sky-500",    gradient: "from-sky-500 to-blue-500" },
  { color: "bg-rose-500",   gradient: "from-rose-500 to-pink-500" },
  { color: "bg-amber-500",  gradient: "from-amber-500 to-yellow-500" },
  { color: "bg-lime-500",   gradient: "from-lime-500 to-green-500" },
  { color: "bg-fuchsia-500",gradient: "from-fuchsia-500 to-purple-500" },
];

// ─── Cache ───────────────────────────────────────────────────────────────────
// Icons are functions and can't survive JSON serialization. We strip them on
// write and re-apply them from MODULE_ICON_MAP on read.

const CACHE_KEY = "scholar_subject_structure";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

type SerializableModule = Omit<Module, "icon"> & { iconKey: string };
type SerializableSubject = Omit<Subject, "modules"> & { modules: SerializableModule[] };
interface CacheEntry { ts: number; data: SerializableSubject[] }

function applyIcons(data: SerializableSubject[]): Subject[] {
  return data.map(s => ({
    ...s,
    modules: s.modules.map(m => ({
      ...m,
      icon: MODULE_ICON_MAP[m.iconKey] ?? BookOpen,
    })),
  }));
}

function readCache(): Subject[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL) return null;
    return applyIcons(entry.data);
  } catch {
    return null;
  }
}

function writeCache(data: Subject[]) {
  try {
    const serializable: SerializableSubject[] = data.map(s => ({
      ...s,
      modules: s.modules.map(m => ({
        ...m,
        icon: undefined as any,
        iconKey: m.id,
      })),
    }));
    const entry: CacheEntry = { ts: Date.now(), data: serializable };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {}
}

export function invalidateSubjectCache() {
  localStorage.removeItem(CACHE_KEY);
}

// ─── Fetch & build ───────────────────────────────────────────────────────────

interface Row {
  subjectId: string;
  moduleId: string;
  topicId: string;
  subtopicId?: string;
}

async function fetchAllRows(): Promise<Row[]> {
  const rows: Row[] = [];
  let cursor: string | null = null;

  while (true) {
    const queries = [
      Query.select(["subjectId", "moduleId", "topicId", "subtopicId", "isActive"]),
      Query.equal("isActive", true),
      Query.limit(5000),
    ];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const res = await databases.listDocuments(DATABASE_ID, COLLECTIONS.QUESTIONS, queries);
    for (const doc of res.documents) {
      rows.push({
        subjectId: doc.subjectId,
        moduleId:  doc.moduleId,
        topicId:   doc.topicId,
        subtopicId: doc.subtopicId ?? undefined,
      });
    }

    if (res.documents.length < 5000) break;
    cursor = res.documents[res.documents.length - 1].$id;
  }

  return rows;
}

function buildStructure(rows: Row[]): Subject[] {
  // subject → module → topic → subtopics + counts
  const tree = new Map<string, Map<string, Map<string, Set<string>>>>();
  const counts = new Map<string, number>(); // "subjectId|moduleId|topicId" → count

  for (const r of rows) {
    if (!tree.has(r.subjectId)) tree.set(r.subjectId, new Map());
    const mods = tree.get(r.subjectId)!;
    if (!mods.has(r.moduleId)) mods.set(r.moduleId, new Map());
    const topics = mods.get(r.moduleId)!;
    if (!topics.has(r.topicId)) topics.set(r.topicId, new Set());
    if (r.subtopicId) topics.get(r.topicId)!.add(r.subtopicId);

    const key = `${r.subjectId}|${r.moduleId}|${r.topicId}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  let colorIdx = 0;
  const subjects: Subject[] = [];

  for (const [subjectId, modsMap] of tree) {
    const meta = SUBJECT_META[subjectId];
    const fallback = DEFAULT_COLORS[colorIdx++ % DEFAULT_COLORS.length];

    const modules: Module[] = [];
    for (const [moduleId, topicsMap] of modsMap) {
      const icon = MODULE_ICON_MAP[moduleId] ?? BookOpen;
      const topics: Topic[] = [];

      for (const [topicId, subtopicsSet] of topicsMap) {
        const subtopics: Subtopic[] = [...subtopicsSet].map((sid) => ({
          id: sid,
          name: idToName(sid),
          questionCount: 0,
        }));
        topics.push({
          id: topicId,
          name: idToName(topicId),
          questionCount: counts.get(`${subjectId}|${moduleId}|${topicId}`) ?? 0,
          subtopics: subtopics.length > 0 ? subtopics : undefined,
        });
      }

      modules.push({
        id: moduleId,
        title: idToName(moduleId),
        icon,
        topics,
      });
    }

    subjects.push({
      id: subjectId,
      title: meta?.description ? idToName(subjectId) : idToName(subjectId),
      description: meta?.description ?? "",
      color: meta?.color ?? fallback.color,
      gradient: meta?.gradient ?? fallback.gradient,
      modules,
    });
  }

  // Stable sort by known order, then alphabetically
  const ORDER = ["sat", "act", "ap-physics-1", "ap-physics-2", "ap-calc-bc", "ap-calc-ab"];
  subjects.sort((a, b) => {
    const ai = ORDER.indexOf(a.id);
    const bi = ORDER.indexOf(b.id);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.title.localeCompare(b.title);
  });

  return subjects;
}

export async function fetchSubjectStructure(): Promise<Subject[]> {
  const cached = readCache();
  if (cached) return cached;

  const rows = await fetchAllRows();
  const structure = buildStructure(rows);
  writeCache(structure);
  return structure;
}
